import { HoneycombWebSDK } from '@honeycombio/opentelemetry-web';
import { trace } from '@opentelemetry/api';

// These must be sent from the browser and therefore will be exposed no matter what.
// Since they are ingest keys, they only have permission to send events.
const honeycombApiKeys = {
    local: 'F2g1zJqjhRiallLAIBimtC',
    dev: 'LOx0lfPGNSkKQmWtZZMmfF',
    qa: 'Jvh1kGhDLz5C4BOj2egeAP',
    stage: 'cI6yXcIcZIigbffdSaKAPN',
    prod: '5Kf9XhTm2AMvYG9IQTZFwB',
};

function stringify(value) {
    return typeof value === 'string' ? value : JSON.stringify(value);
}

function setErrorAttributes(span, error, kind) {
    const config = error?.config;
    const meta = error?.response?.data?.meta;
    span.setAttributes({
        ...(kind && { 'exception.error_kind': kind }),
        'exception.message': stringify(error.message),
        'exception.request.method': stringify(config?.method)?.toUpperCase(),
        'exception.request.url': stringify(config?.url),
        'exception.request.params': stringify(config?.params),
        'exception.request.data': stringify(config?.data),
        'exception.response.type': stringify(meta?.exception_type),
        'exception.response.message': stringify(meta?.message),
        'exception.response.status_code': stringify(meta?.status_code),
        'exception.response.problem_id': stringify(meta?.problem_identifier),
        'exception.response.request_id': stringify(meta?.request_id),
    });
}

function trackVueWarning(message, ...args) {
    const tracer = trace.getTracer('vue-warnings');
    tracer.startActiveSpan('vue-warning', (span) => {
        span.setAttributes({
            'warning.message': message,

            // Filter out arrays and objects that we can't expand in Honeycomb. This will still
            // contain the app name and all the component names
            'warning.stacktrace': (args || []).filter((arg) => typeof arg === 'string').join(' '),
        });
        span.end();
    });
}

export function reportErrorSpan(error, kind) {
    const tracer = trace.getTracer('app-errors');
    tracer.startActiveSpan('app-error', (span) => {
        setErrorAttributes(span, error, kind);
        span.end();
    });
}

export function initHoneycomb() {
    const { hostname } = window.location;
    // Matches both apexes: the S3 deploy serves *.thisiscareerconnect.com and
    // the Foundation deploy serves fdn-app.{env}.discoveryeducation.com. Without
    // the second, Foundation's non-prod hosts fall through to the prod key.
    const envRegexResults = /(dev|qa|stage)\.(?:thisiscareerconnect|discoveryeducation)\.com$/i.exec(hostname);

    const env = envRegexResults ? envRegexResults[1] : hostname === 'localhost' ? 'local' : 'prod';

    // During development, log Vue warnings to Honeycomb
    if (env === 'local' || env === 'dev') {
        const consoleWarn = window.console.warn;
        window.console.warn = function warn(...args) {
            consoleWarn(...args);
            if (args?.[0]?.includes('[Vue warn]')) {
                trackVueWarning(...args);
            }
        };
    }

    // HoneycombWebSDK automatically includes instrumentations for Web Vitals
    const sdk = new HoneycombWebSDK({
        apiKey: honeycombApiKeys[env],
        serviceName: 'careers-app-fe',
        globalErrorsInstrumentationConfig: {
            applyCustomAttributesOnSpan(span, error) {
                setErrorAttributes(span, error);
            },
        },
    });

    sdk.start();
}
